/**
 * The single way this app talks to its backend.
 *
 * Before this module, 23 components each built
 * `https://${projectId}.supabase.co/functions/v1/make-server-a1a36e6d/...` by
 * hand and called `fetch` directly -- 60 call sites with no shared error
 * handling, no timeouts and no way to point a build at a different backend.
 * Everything now goes through `apiRequest`, which:
 *
 *  - resolves the base URL from `config/env`, so a staging build reaches its own
 *    edge function without a code change;
 *  - attaches the right credential: the admin session token for `/admin/*`, the
 *    public anon key for everything else;
 *  - turns a non-2xx response into a typed `ApiError` carrying the status and
 *    the server's message, instead of leaving each caller to guess;
 *  - aborts on a timeout rather than hanging forever.
 */
import { env } from '@/config/env';
import { errorMessage } from '@/utils/errors';

/** A request that reached the server and came back non-2xx. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The server rejected the caller's credentials. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

/** A request that never got an answer -- offline, DNS, CORS, timeout. */
export class NetworkError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialised as JSON unless it is already a FormData. */
  body?: unknown;
  /**
   * Which credential to send. `admin` requires a live Supabase session belonging
   * to an allow-listed account and is mandatory for every `/admin/*` path.
   */
  auth?: 'admin' | 'public' | 'none';
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Milliseconds before the request is aborted. */
  timeoutMs?: number;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/*
 * The path prefixes the edge function puts behind `requireAdmin`. Mirrors the
 * `api.use(...)` lines in supabase/functions/_shared/app.ts.
 *
 * This was `path.startsWith('/admin')` alone, which quietly missed the resume
 * download: the server guards `/download/resume/*` too, so the admin panel sent
 * the public anon key and every CV came back 401 `anon_key_is_not_a_session`.
 * Keep the two lists in step -- a route added to one and not the other fails at
 * runtime, in the panel, and nowhere else.
 */
const ADMIN_GUARDED_PREFIXES = ['/admin', '/download/resume'] as const;

/** Whether the server will demand an admin session for this path. */
function needsAdminSession(path: string): boolean {
  return ADMIN_GUARDED_PREFIXES.some((prefix) => path.startsWith(prefix));
}

const DEFAULT_TIMEOUT_MS = 30_000;

/** Distinguishes our own timeout from a caller's abort on the shared controller. */
const TIMEOUT_REASON = Symbol('api-timeout');

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function authorization(auth: NonNullable<RequestOptions['auth']>): Promise<string | null> {
  switch (auth) {
    case 'admin': {
      /*
       * Imported here rather than at the top of the file. utils/adminAuth pulls
       * in the Supabase client, and importing it eagerly put ~200KB of
       * supabase-js into the entry chunk -- downloaded by every visitor to a
       * marketing page, none of whom will ever make an admin request.
       *
       * Throws AdminAuthError and fires the sign-out event if the session is
       * gone.
       */
      const { getAdminToken } = await import('@/utils/adminAuth');
      return `Bearer ${await getAdminToken()}`;
    }
    case 'public':
      return `Bearer ${env.anonKey}`;
    case 'none':
      return null;
  }
}

/**
 * The field names from a schema rejection, appended to the message.
 *
 * `readJson` answers `{ error: 'Invalid request', fields: { name: reason } }`,
 * and the UI showed only the first half. "Invalid request" on its own is a dead
 * end -- it cost three separate debugging sessions to learn that the field was
 * a job type two characters over its cap, then an unsent agreeToTerms. The
 * names are the client's own, so there is nothing here it did not just send.
 */
function offendingFields(body: object): string {
  const fields = (body as { fields?: unknown }).fields;
  if (!fields || typeof fields !== 'object') return '';
  const names = Object.keys(fields as Record<string, unknown>);
  return names.length ? ` (${names.join(', ')})` : '';
}

/**
 * The shape the edge function uses for failures: `{ error: "..." }`, sometimes
 * with a longer `message` beside it.
 *
 * `message` is read first: where both are present the submission routes put a
 * deliberately generic line in `error` ("Unable to process submission") and the
 * explanation of what to do about it in `message`, so preferring `error` left
 * applicants staring at a dead end.
 */
function serverMessage(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    for (const key of ['message', 'error', 'detail'] as const) {
      const value = (body as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.trim()) return `${value}${offendingFields(body)}`;
    }
  }
  /*
   * A plain-text body is only worth surfacing if it looks like a message. A
   * gateway that answers with an HTML error page would otherwise have its markup
   * shown to the user verbatim.
   */
  if (typeof body === 'string') {
    const text = body.trim();
    if (text && text.length <= 200 && !text.startsWith('<')) return text;
  }
  return `Request failed with status ${status}`;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    method = 'GET',
    body,
    auth = needsAdminSession(path) ? 'admin' : 'public',
    query,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal,
    headers: extraHeaders,
  } = options;

  if (needsAdminSession(path) && auth !== 'admin') {
    throw new Error(`Refusing to call ${path} without an admin session.`);
  }

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers: Record<string, string> = { ...extraHeaders };

  const credential = await authorization(auth);
  if (credential) headers.Authorization = credential;
  // Let the browser set the multipart boundary; naming the type here breaks it.
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';

  /*
   * One controller for both reasons a request can be cancelled. AbortSignal.any
   * would express this directly but only landed in Safari 17.4, and reaching for
   * it made every request throw on anything older.
   */
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(TIMEOUT_REASON), timeoutMs);
  const forwardAbort = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', forwardAbort, { once: true });
  if (signal?.aborted) forwardAbort();

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    // A caller-initiated abort is not a failure; let it propagate as-is.
    if (signal?.aborted) throw error;
    if (controller.signal.reason === TIMEOUT_REASON) {
      throw new NetworkError(`${method} ${path} timed out after ${timeoutMs}ms`, error);
    }
    throw new NetworkError(errorMessage(error, 'Could not reach the server'), error);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', forwardAbort);
  }

  const raw = await response.text();
  let parsed: unknown = raw;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Leave it as text; an HTML error page from the gateway lands here.
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, serverMessage(parsed, response.status), parsed);
  }

  return parsed as T;
}

/** URL of a resource fetched by the browser directly (an <img> src). */
export function apiUrl(path: string, query?: RequestOptions['query']): string {
  return buildUrl(path, query);
}

/**
 * A binary resource, as a Blob.
 *
 * Separate from `apiRequest` because that parses the body as JSON. Resume
 * downloads cannot simply be an <a href>: Supabase's gateway wants the
 * Authorization header, which a plain navigation does not send.
 */
export async function apiBlob(
  path: string,
  options: Omit<RequestOptions, 'body' | 'method'> = {}
): Promise<Blob> {
  const { auth = needsAdminSession(path) ? 'admin' : 'public', query, signal } = options;

  const headers: Record<string, string> = {};
  const credential = await authorization(auth);
  if (credential) headers.Authorization = credential;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), { headers, signal });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new NetworkError(errorMessage(error, 'Could not reach the server'), error);
  }

  if (!response.ok) {
    // The error body is small and textual even when the success body is not.
    const text = await response.text().catch(() => '');
    throw new ApiError(response.status, serverMessage(text, response.status), text);
  }

  return response.blob();
}
